/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-namespace */
import { customElement } from 'solid-element';
import CustomInput, { type CustomInputProps } from './CustomInput';
import { ICustomElement, noShadowDOM } from 'component-register';
import { Show } from 'solid-js';

// Extended interface for web component with imperative methods
export interface CustomInputElement extends HTMLElement {
  focus(): void;
}

/**
 * Usage example for imperative focus:
 *
 * const inputRef = useRef<HTMLElement>();
 *
 * // Call focus method directly on the element
 * const handleFocus = () => {
 *   if (inputRef.current && 'focus' in inputRef.current) {
 *     (inputRef.current as CustomInputElement).focus();
 *   }
 * };
 *
 * <tc-custom-input ref={inputRef} name="example" label="Example" />
 */

// TypeScript declaration for the web component
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'tc-custom-input': Partial<CustomInputElement> &
        CustomInputProps &
        Partial<{ ref: ICustomElement }>;
    }
  }
}

export function defineSolidCustomInput(useShadowDOM = false): void {
  customElement(
    'tc-custom-input',
    {
      // Include ALL component properties with default values (excluding setFocus)
      name: '',
      label: '',
      id: undefined as unknown as string | undefined,
      value: undefined as unknown as string | undefined,
      placeholder: undefined as unknown as string | undefined,
      type: 'text' as string,
      disabled: false,
      readonly: false,
      required: false,
      class: undefined as unknown as string | undefined,
      'data-cy': undefined as unknown as string | undefined,
      dataCy: undefined as unknown as string | undefined,
      error: undefined as unknown as string | undefined,
      touched: false,

      // Event props present for completeness; events are dispatched as CustomEvents
      onInput: undefined as unknown as CustomInputProps['onInput'],
      onChange: undefined as unknown as CustomInputProps['onChange'],
      onBlur: undefined as unknown as CustomInputProps['onBlur'],
      onFocus: undefined as unknown as CustomInputProps['onFocus'],
      onKeyDown: undefined as unknown as CustomInputProps['onKeyDown'],
      onInputRef: undefined as unknown as CustomInputProps['onInputRef'],
    },
    (props: CustomInputProps, { element }) => {
      if (!useShadowDOM) {
        noShadowDOM();
      }

      let inputRef: HTMLInputElement | undefined;

      // Attach focus method to the web component element for imperative calling
      (element as unknown as CustomInputElement).focus = () => {
        if (inputRef) {
          inputRef.focus();
        }
      };

      return (
        <>
          <Show when={useShadowDOM}>
            <link
              rel="stylesheet"
              href="https://resources.connect.trimble.com/1.24.0/css/style.min.css"
            />
          </Show>

          <CustomInput
            {...props}
            onInputRef={(ref) => {
              inputRef = ref;
              if (props.onInputRef) {
                props.onInputRef(ref);
              }
            }}
          />
        </>
      );
    }
  );
}

export type { CustomInputProps };
